/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { Button, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { getDefaultValues, PropertyDefinition } from 'componentregistry';
import React, { useRef } from 'react';
import { multiValueSelector } from '../state';
import PropertyField from './PropertyField';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  label: {
    flex: 1,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
}));

function MultiValue({
  label,
  resetValue,
}: {
  label: string;
  resetValue: () => void;
}) {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.label}>{label}</div>
      <span>••• Multiple Values •••</span>
      <Button variant="text" onClick={resetValue}>
        Reset
      </Button>
    </div>
  );
}

function DeferredEditor<T extends PropertyDefinition>(
  props: PropertyEditorProps<T, unknown>,
) {
  const {
    appUid,
    mgr,
    pageId,
    def,
    property,
    value: val = getDefaultValues<unknown>(mgr, property),
    onChange,
  } = props;
  const undoableRef = useRef(true);
  const { sameValue, multiEdit } = useStateXSelector(multiValueSelector, {
    params: { appUid, pageId, property },
  });

  if (multiEdit && !sameValue) {
    return (
      <MultiValue
        resetValue={() => {
          const defaultValue = getDefaultValues<string>(mgr, property);
          onChange(property, defaultValue, true);
          undoableRef.current = false;
        }}
        label={def.label}
      />
    );
  }

  return <PropertyField {...props} value={val} onChange={onChange} />;
}

export default DeferredEditor;
